import React, {useEffect, useState} from "react";
import {Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement} from 'chart.js';
import {Card, Col, Layout, message, Result, Row, Segmented, Spin, Statistic, Tabs} from "antd";
import {CheckCircleOutlined, CloseCircleOutlined} from '@ant-design/icons';
import {Bar, Pie} from "react-chartjs-2";
import {COLORS} from "../utils/Colors";
import {StatisticsControllerApi, TestPlanStatistic, TestRunStatistic} from "../api";
import {useKeycloakHeader} from "../config/Config";
import {useKeycloak} from "@react-keycloak/web";
import logo from "../assets/btcLogoBlau.png";

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

const {Content} = Layout;
const {TabPane} = Tabs;

const Dashboard = () => {
    const {keycloak} = useKeycloak();
    const headers = useKeycloakHeader();
    const api = new StatisticsControllerApi();
    const [dateFilter, setDateFilter] = useState<number>(7);
    const [testRunStatistic, setTestRunStatistic] = useState<TestRunStatistic>();
    const [testPlanStatistic, setTestPlanStatistic] = useState<TestPlanStatistic[]>([]);
    const [testPlanDataChart, setTestPlanDataChart] = useState<any>({labels: [], datasets: []});
    const [testRunDataChart, setTestRunDataChart] = useState<any>({labels: [], datasets: []});


    useEffect(() => {
        console.log("Dashboard: useEffect");
        if(keycloak.authenticated){
            console.log("Dashboard: useEffect: keycloak.authenticated");
            fetchApi();
        }
    }, [dateFilter]);

    const fetchApi = () => {
        fetchTestRunStatistic();
        fetchTestPlanStatistic();
    }

    const fetchTestPlanStatistic = () => {
        let testPlanAmount = 4;
        api.getTestPlanStatistics(dateFilter, testPlanAmount, headers).then((response) => {
            let statistic = response.data;
            setTestPlanStatistic(statistic);
            let dataForChart = getTestPlanDataForChart(statistic);
            setTestPlanDataChart(dataForChart);
        }).catch((error) => {
            message.error("Es ist ein Fehler bei der Abfrage der Testlaufdaten aufgetreten!");
        })
    }

    const fetchTestRunStatistic = () => {
        api.getTestRunStatistics(dateFilter, headers).then((response) => {
            let runStatistic = response.data;
            setTestRunStatistic(runStatistic);
            let pieData = getTestRunDataForChart(runStatistic);
            setTestRunDataChart(pieData);
        }).catch((error) => {
            message.error("Es ist ein Fehler bei der Abfrage der Testlaufdaten aufgetreten!");
        })
    }

    const getTestRunDataForChart = (data: TestRunStatistic) => {
        const pieData = {
            labels: ['Erfolgreich', 'Fehlgeschlagen'],
            datasets: [
                {
                    data: [data.successFullTestRuns, data.failedFullTestRuns],
                    backgroundColor: [COLORS.btcPrimaryMediumBlue, COLORS.btcTertiaryPink],
                },
            ],
        };
        return pieData;
    }

    const getTestPlanDataForChart = (statistic: TestPlanStatistic[]) => {
        let labels = [] as string[];
        let successData = [] as number[];
        let failData = [] as number[];

        statistic.forEach(t => {
            // Füge den Namen des Testplans hinzu (oder 'Unbekannt', wenn der Name fehlt)
            labels.push(t?.testPlan?.name || 'Unbekannt');

            // Berechne die Gesamtzahl der Tests
            const totalTests = (t.success || 0) + (t.fail || 0);

            // Berechne die Erfolgsrate und Fehlschlagsrate
            const successRate = (totalTests > 0 && t.success) ? (t.success / totalTests) * 100 : 0;  // Wenn keine Tests, Erfolgsrate 0%
            const failRate = (totalTests > 0 && t.fail) ? (t.fail / totalTests) * 100 : 0;  // Wenn keine Tests, Fehlschlagsrate 0%

            // Füge die berechneten Werte in die Arrays hinzu
            successData.push(successRate);
            failData.push(failRate);
        });

        const testPlanData = {
            labels: labels,
            datasets: [
                {
                    label: 'Erfolgreich',
                    data: successData,  // Erfolgsraten
                    backgroundColor: COLORS.btcPrimaryMediumBlue,  // Beispiel-Farbe für den Erfolgs-Datensatz
                },
                {
                    label: 'Fehlgeschlagen',
                    data: failData,     // Fehlschlagsraten
                    backgroundColor: COLORS.btcTertiaryPink,  // Beispiel-Farbe für den Fehlschlags-Datensatz
                },
            ],
        };
        return testPlanData;
    }

    const handleTabChange = (key) => {
        if (key === "1 Tag") {
            setDateFilter(1);
        } else if (key === "7 Tage") {
            setDateFilter(7);
        } else if (key === "30 Tage") {
            setDateFilter(30);
        }

    };

    const notAuthenticated = () => {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                flexDirection: 'column',
                backgroundColor: COLORS.btcPrimaryGrey
            }}>
                <img src={logo} alt="Logo" style={{width: 150, marginBottom: 20, marginLeft: 15}}/>
                <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                    <h2>Nicht angemeldet</h2>
                    <p>Bitte melden Sie sich an, um auf das Dashboard zugreifen zu können.</p>
                </div>
            </div>
        );
    }

    const dashboard = () => {
        return (
            <Layout style={{padding: '0 24px 24px'}}>
                <Content style={{margin: '24px 0'}}>
                    {/* Custom Header */}
                    <Row>
                        <Col span={24}>
                            <h2>Dashboard</h2>
                        </Col>
                    </Row>

                    <Row gutter={16} style={{marginBottom: 24}}>
                        <Col span={24}>
                            <Segmented<string>
                                defaultValue={'7 Tage'}
                                options={['1 Tag', '7 Tage', '30 Tage']}
                                onChange={(value) => {
                                    handleTabChange(value);
                                }}
                            />
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Card>
                                <Statistic
                                    title="Erfolgreiche Testpläne"
                                    value={testRunStatistic?.successFullTestRuns}
                                    valueStyle={{color: '#3f8600'}}
                                    prefix={<CheckCircleOutlined/>}
                                />
                            </Card>
                        </Col>
                        <Col span={12}>
                            <Card>
                                <Statistic
                                    title="Fehlgeschlagene Testpläne"
                                    value={testRunStatistic?.failedFullTestRuns}
                                    valueStyle={{color: '#cf1322'}}
                                    prefix={<CloseCircleOutlined/>}
                                />
                            </Card>
                        </Col>
                    </Row>

                    <Row gutter={16} style={{marginTop: 24}}>
                        <Col span={12}>
                            <Card title="Testpläne Erfolgsrate" style={{height: '400px'}}>
                                <Bar
                                    data={testPlanDataChart}
                                    options={{
                                        maintainAspectRatio: false,
                                        scales: {
                                            y: {beginAtZero: true},
                                        },
                                        plugins: {
                                            legend: {position: 'top'},
                                            tooltip: {enabled: true},
                                        },
                                    }}
                                />
                            </Card>
                        </Col>
                        <Col span={12}>
                            <Card title="Gesamtübersicht Testausführungen" style={{height: '400px'}}>
                                <Pie
                                    style={{height: '300px'}}
                                    data={testRunDataChart}
                                    options={{
                                        maintainAspectRatio: false,
                                        plugins: {
                                            legend: {position: 'top'},
                                            tooltip: {enabled: true},
                                        },
                                    }}
                                />
                            </Card>
                        </Col>
                    </Row>
                </Content>
            </Layout>
        );
    }

    return keycloak.authenticated ? dashboard() : notAuthenticated();

};

export default Dashboard;
